import { useEffect, useState, RefObject } from "react";
import throttle from "lodash-es/throttle";

/**
 * 容器位置信息接口
 */
export interface ContainerPosition {
  width: number;
  left: number; // 距离视口左边的距离
  right: number; // 距离视口右边的距离
  isReady: boolean; // 是否已获取到容器信息
}

/**
 * useContainerPosition Hook 的配置选项
 */
export interface UseContainerPositionOptions {
  /**
   * 容器的 ref（优先使用）
   */
  containerRef?: RefObject<HTMLElement | HTMLDivElement | null> | null;
  /**
   * 容器的选择器（当 ref 未提供时使用）
   */
  selector?: string;
  /**
   * 是否启用监听（默认 true）
   */
  enabled?: boolean;
  /**
   * 刷新触发器：当此值变化时，强制更新位置信息
   * 用于监听位置变化（而非尺寸变化）的场景，如侧边栏展开/收起
   */
  refreshTrigger?: unknown;
}

/**
 * 获取容器位置和宽度的自定义 Hook
 *
 * 通过 ref 或选择器查找目标容器，获取其宽度和距离视口左右的距离。
 * 使用 ResizeObserver 和 window.resize 监听变化，自动更新位置信息。
 *
 * @param options - 配置选项
 * @returns 容器的位置和宽度信息
 *
 * @example
 * ```tsx
 * // 使用 ref
 * const containerRef = useRef<HTMLDivElement>(null);
 * const position = useContainerPosition({ containerRef });
 *
 * // 使用选择器
 * const position = useContainerPosition({
 *   selector: '[data-container="main-content"]'
 * });
 * ```
 */
export const useContainerPosition = (
  options: UseContainerPositionOptions = {},
): ContainerPosition => {
  const {
    containerRef,
    selector = '[data-container="main-content"]',
    enabled = true,
    refreshTrigger,
  } = options;

  const [position, setPosition] = useState<ContainerPosition>({
    width: 0,
    left: 0,
    right: 0,
    isReady: false,
  });

  useEffect(() => {
    if (!enabled) {
      return;
    }

    // 查找目标容器
    const findContainer = (): HTMLElement | null => {
      // 优先使用 ref
      if (containerRef?.current) {
        return containerRef.current;
      }

      // 如果 ref 不存在，使用选择器查找（仅在客户端环境）
      if (typeof window !== "undefined" && selector) {
        return document.querySelector<HTMLElement>(selector);
      }

      return null;
    };

    const updatePosition = () => {
      const container = findContainer();
      if (!container) {
        setPosition({
          width: 0,
          left: 0,
          right: 0,
          isReady: false,
        });
        return;
      }

      const rect = container.getBoundingClientRect();
      setPosition({
        width: rect.width,
        left: rect.left,
        right: window.innerWidth - rect.right,
        isReady: true,
      });
    };

    // 使用 throttle 优化性能
    const throttledUpdatePosition = throttle(updatePosition, 80, {
      leading: true,
      trailing: true,
    });

    // 初始计算
    updatePosition();

    // 延迟执行，确保 DOM 已渲染
    const timeoutId = setTimeout(() => {
      updatePosition();
    }, 0);

    // 创建 ResizeObserver 来监听容器尺寸变化
    const container = findContainer();
    let resizeObserver: ResizeObserver | null = null;

    if (container) {
      resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          if (entry.target === container) {
            throttledUpdatePosition();
          }
        }
      });

      resizeObserver.observe(container);
    }

    // 监听窗口大小变化和滚动（作为备用方案）
    const handleResize = () => {
      throttledUpdatePosition();
    };

    const handleScroll = () => {
      throttledUpdatePosition();
    };

    window.addEventListener("resize", handleResize);
    window.addEventListener("scroll", handleScroll, { passive: true });

    // 清理函数
    return () => {
      clearTimeout(timeoutId);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("scroll", handleScroll);
      throttledUpdatePosition.cancel();
    };
  }, [containerRef, selector, enabled, refreshTrigger]);

  // 当 refreshTrigger 变化时，延迟更新位置（等待 DOM 布局完成）
  useEffect(() => {
    if (!enabled || refreshTrigger === undefined) {
      return;
    }

    // 查找目标容器
    const findContainer = (): HTMLElement | null => {
      if (containerRef?.current) {
        return containerRef.current;
      }
      if (typeof window !== "undefined" && selector) {
        return document.querySelector<HTMLElement>(selector);
      }
      return null;
    };

    const updatePosition = () => {
      const container = findContainer();
      if (!container) {
        return;
      }

      const rect = container.getBoundingClientRect();
      setPosition({
        width: rect.width,
        left: rect.left,
        right: window.innerWidth - rect.right,
        isReady: true,
      });
    };

    // 使用 requestAnimationFrame 确保在下一帧更新，等待 DOM 布局完成
    let timeoutId: NodeJS.Timeout | null = null;
    const rafId = requestAnimationFrame(() => {
      // 再延迟一小段时间，确保布局已完成
      timeoutId = setTimeout(() => {
        updatePosition();
      }, 100);
    });

    return () => {
      cancelAnimationFrame(rafId);
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [refreshTrigger, enabled, containerRef, selector]);

  return position;
};
